import React, { useEffect, useState, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import Page from '../../../components/Page';
import Breadcrumb from '../../../components/Breadcrumb';
import client from '../../../lib/feathers';

const PAGE_SIZE = 25;

// Wines with no bottle/label image (on the wine or any vintage), most-sipped first.
// The drill-down behind the "Wines with image" coverage metric on the dashboard.
export default function LibationsWithoutImages() {
  const [res, setRes] = useState(null);
  const [error, setError] = useState(null);
  const [skip, setSkip] = useState(0);

  const load = useCallback(async (nextSkip) => {
    try {
      setError(null);
      const r = await client.service('admin/libations-without-images').find({
        query: { $limit: PAGE_SIZE, $skip: nextSkip },
      });
      setRes(r);
      setSkip(nextSkip);
    } catch (e) {
      setError('Failed to load report.');
    }
  }, []);

  useEffect(() => { load(0); }, [load]);

  const total = res ? res.total : 0;
  const rows = (res && res.data) || [];
  const from = total === 0 ? 0 : skip + 1;
  const to = Math.min(skip + PAGE_SIZE, total);

  return (
    <Page title="Reports">
      <div className="pb-5 border-b border-gray-200">
        <Breadcrumb elements={[
          { name: 'Reports', href: '/admin/reports' },
          { name: 'Wines Without Images' },
        ]}
        />
      </div>

      <div className="py-5">
        <h1 className="text-2xl pb-1">Wines Without Images</h1>
        <p className="text-sm text-gray-500 max-w-3xl">
          Wines with no bottle or label image (on the wine or any of its vintages),
          most-sipped first — so the most-seen gaps are at the top. Includes
          user-library wines that have no listing.
        </p>
      </div>

      {error && <div className="text-sm text-red-700 mb-4">{error}</div>}
      {!error && res === null && <div className="text-sm text-gray-500">Loading…</div>}

      {!error && res && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <strong>{total.toLocaleString()}</strong> wines without images
              {total > 0 && <span className="text-gray-400"> · showing {from}–{to}</span>}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => load(Math.max(skip - PAGE_SIZE, 0))}
                disabled={skip === 0}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-40"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => load(skip + PAGE_SIZE)}
                disabled={to >= total}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>

          {rows.length === 0 ? (
            <div className="p-6 text-sm text-gray-500 italic">No wines without images. 🎉</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Wine</th>
                  <th className="px-4 py-2 font-medium">Listing</th>
                  <th className="px-4 py-2 font-medium">Source</th>
                  <th className="px-4 py-2 font-medium text-right">Sips</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((l) => (
                  <tr key={l.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <span className="font-medium text-gray-900">{l.name}</span>
                      {l.brand && <span className="ml-2 text-xs text-gray-400">brand: {l.brand}</span>}
                    </td>
                    <td className="px-4 py-2">
                      {l.listingId ? (
                        <NavLink to={`/admin/listings/${l.listingId}`} className="text-indigo-700 hover:underline">
                          {l.listingName || 'listing'}
                        </NavLink>
                      ) : (
                        <span className="text-amber-600">no listing</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-500">{l.source}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                      {Number(l.totalSips).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Page>
  );
}
